import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronRight,
  FilePlus,
  FileSignature,
  Search,
} from "lucide-react";
import { useOrg } from "@/lib/org/OrgContext";
import { ApiError, apiFetch } from "@/api/fetch";
import { companiesQueries } from "@/lib/companies/api";
import {
  ContractStatus,
  statusLabel,
  type ContractStatus as ContractStatusValue,
} from "@/lib/contracts/api";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatRelative } from "@/lib/format";

interface ContractSummaryDto {
  id: string;
  status: ContractStatusValue;
  contractType: string;
  employeeDisplayName: string;
  companyName: string;
  createdAt: string;
  employmentId: string | null;
  kind: "NewContract" | "Amendment" | "Adoption";
}

type RowWithCompany = ContractSummaryDto & { companyId: string };

type StatusFilter = "all" | "open" | ContractStatusValue;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "open", label: "Offen" },
  { value: ContractStatus.Draft, label: "Entwürfe" },
  { value: ContractStatus.SendFailed, label: "Versandfehler" },
  { value: ContractStatus.Sent, label: "Versendet" },
  { value: ContractStatus.Signed, label: "Unterschrieben" },
];

export const Route = createFileRoute("/o/$slug/contracts/")({
  component: ContractsLandingPage,
});

function ContractsLandingPage() {
  const org = useOrg();
  const slug = org.slug;
  const [status, setStatus] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");

  const companies = useQuery(companiesQueries.forOrg(slug));

  // Fan-out: contracts are company-scoped. 1-min staleTime — list view; users
  // visit it knowing what they want and don't expect 5-min lag after a confirm.
  const lists = useQueries({
    queries: (companies.data ?? []).map((c) => ({
      queryKey: ["contracts", "by-company", c.id] as const,
      queryFn: () => apiFetch<ContractSummaryDto[]>(`/api/contracts?companyId=${c.id}`),
      staleTime: 60_000,
      retry: false,
    })),
  });

  const rows = useMemo(() => {
    const all: RowWithCompany[] = [];
    (companies.data ?? []).forEach((c, i) => {
      const data = lists[i]?.data;
      if (data) for (const r of data) all.push({ ...r, companyId: c.id });
    });
    all.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
    return all;
  }, [companies.data, lists]);

  const filtered = useMemo(() => filterRows(rows, status, search), [rows, status, search]);

  const counts = useMemo(() => statusCounts(rows), [rows]);

  const isLoading = companies.isLoading || lists.some((q) => q.isLoading);
  const firstError =
    (companies.error as Error | null) ?? (lists.find((q) => q.error)?.error as Error | undefined) ?? null;

  return (
    <PageContainer>
      <PageHeader
        title="Verträge"
        description="Verträge und Vertragsergänzungen aller Unternehmen dieser Organisation."
        actions={
          <Button asChild>
            <Link to="/o/$slug/contracts/new" params={{ slug }}>
              <FilePlus className="h-4 w-4" />
              Neuer Vertrag
            </Link>
          </Button>
        }
      />

      <FilterBar
        status={status}
        onStatus={setStatus}
        search={search}
        onSearch={setSearch}
        counts={counts}
      />

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      )}

      {!isLoading && firstError && (
        <Card>
          <CardContent className="flex items-start gap-2 p-4 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {firstError instanceof ApiError && firstError.status === 403
                ? "Keine Berechtigung."
                : `Konnte Verträge nicht laden: ${firstError.message}`}
            </span>
          </CardContent>
        </Card>
      )}

      {!isLoading && !firstError && rows.length === 0 && (
        <EmptyState slug={slug} hasCompanies={(companies.data ?? []).length > 0} />
      )}

      {!isLoading && !firstError && rows.length > 0 && filtered.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            Keine Verträge passen zum Filter.
          </CardContent>
        </Card>
      )}

      {filtered.length > 0 && (
        <ul className="divide-y rounded-md border bg-card">
          {filtered.map((c) => (
            <ContractRow key={c.id} contract={c} slug={slug} />
          ))}
        </ul>
      )}
    </PageContainer>
  );
}

// ── Filter bar ────────────────────────────────────────────────────────────

function FilterBar({
  status,
  onStatus,
  search,
  onSearch,
  counts,
}: {
  status: StatusFilter;
  onStatus: (s: StatusFilter) => void;
  search: string;
  onSearch: (s: string) => void;
  counts: Record<string, number>;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap gap-1.5">
        {STATUS_FILTERS.map((f) => {
          const count = counts[String(f.value)];
          const active = status === f.value;
          return (
            <button
              key={String(f.value)}
              type="button"
              onClick={() => onStatus(f.value)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors",
                active
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-card text-foreground hover:bg-muted",
              )}
            >
              <span>{f.label}</span>
              {count !== undefined && count > 0 && (
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
                    active ? "bg-primary/20" : "bg-muted",
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="relative ml-auto w-full max-w-xs">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Mitarbeiter:in suchen…"
          className="pl-8"
        />
      </div>
    </div>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────

function ContractRow({ contract, slug }: { contract: RowWithCompany; slug: string }) {
  return (
    <li>
      <Link
        to="/o/$slug/contracts/$contractId"
        params={{ slug, contractId: contract.id }}
        className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40"
      >
        <FileSignature className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{contract.employeeDisplayName}</span>
            {contract.kind === "Amendment" && (
              <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-blue-700">
                Ergänzung
              </span>
            )}
            {contract.kind === "Adoption" && (
              <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-violet-700">
                Adoption
              </span>
            )}
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {contract.contractType} · {contract.companyName} · {formatRelative(contract.createdAt) ?? "—"}
          </div>
        </div>
        <StatusPill status={contract.status} />
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </Link>
    </li>
  );
}

function StatusPill({ status }: { status: ContractStatusValue }) {
  const cls = statusClass(status);
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium",
        cls,
      )}
    >
      {statusLabel(status)}
    </span>
  );
}

function statusClass(s: ContractStatusValue): string {
  switch (s) {
    case ContractStatus.Draft:
      return "bg-muted text-muted-foreground";
    case ContractStatus.Confirmed:
      return "bg-blue-100 text-blue-700";
    case ContractStatus.Sent:
      return "bg-sky-100 text-sky-700";
    case ContractStatus.SendFailed:
      return "bg-red-100 text-red-700";
    case ContractStatus.Signed:
      return "bg-emerald-100 text-emerald-700";
    case ContractStatus.Archived:
      return "bg-muted text-muted-foreground";
  }
}

// ── Empty state ───────────────────────────────────────────────────────────

function EmptyState({ slug, hasCompanies }: { slug: string; hasCompanies: boolean }) {
  if (!hasCompanies) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <FileSignature className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            Keine Unternehmen in dieser Organisation. Ein Org-Admin muss zunächst einen Ordio-API-Key
            hinterlegen.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-12 text-center">
        <FileSignature className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-sm text-muted-foreground">Noch keine Verträge angelegt.</p>
        <Button asChild className="mt-4">
          <Link to="/o/$slug/contracts/new" params={{ slug }}>
            <FilePlus className="h-4 w-4" />
            Ersten Vertrag anlegen
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function filterRows(
  rows: RowWithCompany[],
  status: StatusFilter,
  search: string,
): RowWithCompany[] {
  const q = search.trim().toLowerCase();
  return rows.filter((r) => {
    if (status === "open") {
      // "Offen" = anything actionable: drafts + send-failed. Sent/Signed/Archived
      // are terminal-enough that they shouldn't appear in the default working set.
      if (r.status !== ContractStatus.Draft && r.status !== ContractStatus.SendFailed) return false;
    } else if (status !== "all" && r.status !== status) {
      return false;
    }
    if (q && !r.employeeDisplayName.toLowerCase().includes(q)) return false;
    return true;
  });
}

function statusCounts(rows: RowWithCompany[]): Record<string, number> {
  const counts: Record<string, number> = { all: rows.length, open: 0 };
  for (const r of rows) {
    counts[String(r.status)] = (counts[String(r.status)] ?? 0) + 1;
    if (r.status === ContractStatus.Draft || r.status === ContractStatus.SendFailed) {
      counts.open += 1;
    }
  }
  return counts;
}
